import type mapboxgl from "mapbox-gl";
import type { Expression } from "mapbox-gl";

/**
 *
 * https://www.aviationweather.gov/help/webservice?page=sigmetjson
 * https://www.aviationweather.gov/help/webservice?page=tafjson
 * https://www.aviationweather.gov/help/webservice?page=metarjson
 * https://www.aviationweather.gov/help/webservice?page=Airepjson
 *
 * All styling was picked out from here.
 * Since these were JavaScript library for the Openlayers,
 * the styles were transferred to Mapbox gl js style expressions.
 *
 * https://www.aviationweather.gov/javascript/awc_openlayers-m.js?vers=1635358916
 * https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/
 *
 * */

export const aviatioinDomain = "https://www.aviationweather.gov";

export const windIconFromProperties: Expression = [
  "concat",
  "n",
  ["to-string", ["-", ["get", "wspd"], ["%", ["get", "wspd"], 5]]],
];

export const gustsWindIconFromProperties: Expression = [
  "concat",
  "n",
  ["to-string", ["-", ["get", "wgst"], ["%", ["get", "wgst"], 5]]],
];

export const sigmetFillColorStyle: string | Expression = [
  "case",
  ["==", ["get", "airSigmetType"], "OUTLOOK"],
  "#FFAA00",
  ["==", ["get", "hazard"], "TS"],
  "#800000",
  ["==", ["get", "hazard"], "TC"],
  "#800000",
  ["==", ["get", "hazard"], "ICE"],
  "#000080",
  ["==", ["get", "hazard"], "TURB"],
  "#A06000",
  ["==", ["get", "hazard"], "VA"],
  "#808080",
  ["==", ["get", "hazard"], "IFR"],
  "#990099",
  "#800000",
];
export const colorHazard = {
  TS: "#800000",
  TC: "#800000",
  ICE: "#000080",
  TURB: "#A06000",
  VA: "#808080",
  REST: "#800000",
};
export const iSigmetFillColorStyle: string | Expression = [
  "case",
  ["==", ["get", "hazard"], "TS"],
  "#800000",
  ["==", ["get", "hazard"], "TC"],
  "#800000",
  ["==", ["get", "hazard"], "ICE"],
  "#000080",
  ["==", ["get", "hazard"], "TURB"],
  "#A06000",
  ["==", ["get", "hazard"], "VA"],
  "#808080",
  "#800000",
];
export const fillColorOpacity = 0.5;

export const sigmetFillLineColorStyle: string | Expression = [
  "case",
  ["==", ["get", "airSigmetType"], "OUTLOOK"],
  "#FFAA00",
  "#800000",
];
export const airepPopupText = (feature: mapboxgl.MapboxGeoJSONFeature): string => {
  const spancol = '<span style="color: #7777CC; font-weight: bold">';
  let text = `<div style='font-size:10px; color: black'><div style='background-color: #1150a0; color: white; font-weight: bold'>${feature.properties?.airepType} ${feature.properties?.acType}</div>`;
  text = `${text + spancol}Obs Time: </span>${feature.properties?.obsTime}<br/>`;
  if (
    feature.properties?.fltlvl !== undefined &&
    (feature.properties?.fltlvlType === undefined ||
      (feature.properties?.fltlvlType !== "UNKN" &&
        feature.properties?.fltlvlType !== "DURC" &&
        feature.properties?.fltlvlType !== "DURD" &&
        feature.properties?.fltlvl !== 0))
  )
    text = `${text + spancol}Flight level: </span>${feature.properties?.fltlvl}<br/>`;
  if (feature.properties?.fltlvlType !== undefined)
    text = `${text + spancol}Flight level type: </span>${feature.properties?.fltlvlType}<br/>`;
  if (feature.properties?.tbInt1 !== undefined)
    text = `${text + spancol}Turb intensity: </span>${feature.properties?.tbInt1}<br/>`;
  if (feature.properties?.tbType1 !== undefined)
    text = `${text + spancol}Turb type: </span>${feature.properties?.tbType1}<br/>`;
  if (feature.properties?.tbFreq1 !== undefined)
    text = `${text + spancol}Turb freqency: </span>${feature.properties?.tbFreq1}<br/>`;
  if (feature.properties?.icgInt1 !== undefined)
    text = `${text + spancol}Icing intensity: </span>${feature.properties?.icgInt1}<br/>`;
  if (feature.properties?.icgType1 !== undefined)
    text = `${text + spancol}Icing type:</span> ${feature.properties?.icgType1}<br/>`;
  if (feature.properties?.wdir !== undefined)
    text = `${text + spancol}Wind: </span>${feature.properties?.wdir} at ${feature.properties?.wspd}kt<br/>`;
  if (feature.properties?.temp !== undefined)
    text = `${text + spancol}Temperature: </span>${feature.properties?.temp}&deg;C<br/> `;
  if (feature.properties?.cloudCvg1 !== undefined)
    text = `${text + spancol}Cloud Cover: </span>${feature.properties?.cloudCvg1}<br/>`;
  if (feature.properties?.cloudTop1 !== undefined)
    text = `${text + spancol}Cloud Top: </span>${feature.properties?.cloudTop1}<br/>`;
  if (feature.properties?.cloudBas1 !== undefined)
    text = `${text + spancol}Cloud Base: </span>${feature.properties?.cloudBas1}<br/>`;
  if (feature.properties?.cloudCvg2 !== undefined)
    text = `${text + spancol}Cloud Cover2: </span>${feature.properties?.cloudCvg2}<br/>`;
  if (feature.properties?.cloudTop2 !== undefined)
    text = `${text + spancol}Cloud Top2: </span>${feature.properties?.cloudTop2}<br/>`;
  if (feature.properties?.cloudBas2 !== undefined)
    text = `${text + spancol}Cloud Base2: </span>${feature.properties?.cloudBas2}<br/>`;
  if (feature.properties?.wxString !== undefined)
    text = `${text + spancol}Weather: </span>${feature.properties?.wxString}<br/>`;
  if (feature.properties?.brkAction !== undefined)
    text = `${text + spancol}Braking Action: </span>${feature.properties?.brkAction}<br/>`;
  text = `${text}<hr style='margin:1px 0 1px 0'/>${spancol}${feature.properties?.airepType}: </span>${feature.properties?.rawOb}<br/></div>`;
  return text;
};

export const airepLabel: string | Expression = [
  "case",

  ["==", ["get", "airepType"], "Urgent PIREP"],
  ["get", "fltlvl"],

  ["all", ["==", ["to-number", ["get", "fltlvl"]], 0], ["!=", ["get", "fltlvlType"], null]],
  ["get", "fltlvlType"],

  ["get", "fltlvl"],
];
export const airepColor: string | Expression = [
  "case",
  ["==", ["get", "tbInt1"], "EXTM"],
  "#cc00cc",
  ["==", ["get", "tbInt1"], "SEV-EXTM"],
  "#cc00cc",
  ["==", ["get", "tbInt1"], "SEV"],
  "#aa0000",
  ["==", ["get", "icgInt1"], "SEV"],
  "#aa0000",
  ["==", ["get", "icgInt1"], "HVY"],
  "#aa0000",
  ["==", ["get", "tbInt1"], "MOD-SEV"],
  "#aa0000",
  ["==", ["get", "icgInt1"], "MOD-SEV"],
  "#aa0000",
  ["==", ["get", "tbType1"], "MWAVE"],
  "#aa0000",
  ["==", ["get", "tbType1"], "LLWS"],
  "#aa0000",
  ["==", ["get", "tbInt1"], "MOD"],
  "#ff8800",
  ["==", ["get", "icgInt1"], "MOD"],
  "#ff8800",
  ["==", ["get", "tbInt1"], "LGT-MOD"],
  "#ff8800",
  ["==", ["get", "icgInt1"], "LGT-MOD"],
  "#ff8800",
  ["==", ["get", "tbInt1"], "LGT"],
  "#00aa00",
  ["==", ["get", "icgInt1"], "LGT"],
  "#00aa00",
  ["==", ["get", "tbInt1"], "SMTH-LGT"],
  "#00aa00",
  ["==", ["get", "icgInt1"], "TRC-LGT"],
  "#00aa00",
  ["==", ["get", "icgInt1"], "TRC"],
  "#00aa00",
  ["==", ["get", "tbInt1"], "NEG"],
  "#0000ff",
  ["==", ["get", "icgInt1"], "NEG"],
  "#0000ff",
  ["==", ["get", "airepType"], "Urgent PIREP"],
  "#aa0000",
  "#000000",
];

export const sigmetLabel: string | Expression = [
  "case",
  ["==", ["get", "hazard"], "CONVECTIVE"],
  "Conv",
  ["==", ["get", "hazard"], "MTN OBSCN"],
  "MtnOb",
  ["==", ["get", "hazard"], "TURB"],
  "Turb",
  ["==", ["get", "hazard"], "ICE"],
  "Icing",
  ["==", ["get", "hazard"], "ASH"],
  "VA",
  ["==", ["get", "hazard"], "IFR"],
  "IFR",
  "--",
];

export const iSigmetLabel: string | Expression = [
  "case",
  ["==", ["get", "hazard"], "ICE"],
  "Ice",
  ["==", ["get", "hazard"], "TURB"],
  "Turb",
  ["==", ["get", "hazard"], "TC"],
  "Trop",
  ["==", ["get", "hazard"], "DS"],
  "Dust",
  ["get", "hazard"],
];

/**
 * Match the aviationweather metar/taf cloud cover string with the Meteomatics Cloud cover icons
 * https://www.aviationweather.gov/metar/help?page=plot
 * Meteomatics Icons in the "Load on demand IconStore"
 *
 * NCD => 0/8
 * CLR => 0/8
 * NSC/SKC => 0/8
 * FEW => (|) 1-2/8
 * SCT => 3-4/8
 * BKN => 5-7/8
 * OVC => 8/8 full
 * OVX => (X)
 * CAVOK = no icon
 *
 */

export const coverIcon: string | Expression = [
  "case",
  ["==", ["get", "cover"], "NSC"],
  "Cloud_cover_0",
  ["==", ["get", "cover"], "SKC"],
  "Cloud_cover_0",
  ["==", ["get", "cover"], "NCD"],
  "Cloud_cover_0",
  ["==", ["get", "cover"], "CLR"],
  "Cloud_cover_0",
  ["==", ["get", "cover"], "FEW"],
  "Cloud_cover_1",
  ["==", ["get", "cover"], "SCT"],
  "Cloud_cover_2",
  ["==", ["get", "cover"], "BKN"],
  "Cloud_cover_6",
  ["==", ["get", "cover"], "OVC"],
  "Cloud_cover_8",
  ["==", ["get", "cover"], "OVX"],
  "Cloud_cover_9",
  ["==", ["get", "cover"], "OVC"],
  "Cloud_cover_8",
  "",
];

export const fltCatColor: string | Expression = [
  "case",
  ["==", ["get", "fltcat"], "VFR"],
  "#00FF00",
  ["==", ["get", "fltcat"], "MVFR"],
  "#1a1dee",
  ["==", ["get", "fltcat"], "IFR"],
  "#f82600",
  ["==", ["get", "fltcat"], "LIFR"],
  "#d65bff",
  "#000000",
];

export function getiSigmetPopupText(feature: mapboxgl.MapboxGeoJSONFeature): string {
  const spancol = '<span style="color: #7777CC; font-weight: bold">';
  let rawtext = feature.properties?.rawSigmet;
  if (rawtext) {
    rawtext = rawtext.replace(/\n/g, "<br/>");
  }

  let text = `<div style='font-size:12px; color: black'><div style='background-color: #1150a0; color: white;font-weight:bold'>${feature.properties?.hazard} SIGMET</div>`;
  text = `${text + spancol}FIR Id: </span>${feature.properties?.firId}<br/>`;
  text = `${text + spancol}FIR Name: </span>${feature.properties?.firName}<br/>`;
  text = `${text + spancol}Hazard: </span>${feature.properties?.hazard} - ${feature.properties?.qualifier}<br/>`;
  text = `${text + spancol}Begins: </span>${feature.properties?.validTimeFrom}<br/>`;
  text = `${text + spancol}Ends: </span>${feature.properties?.validTimeTo}<br/>`;
  if (feature.properties?.top !== undefined) text = `${text + spancol}Top: </span>${feature.properties?.top} ft<br/>`;
  if (feature.properties?.base !== undefined)
    text = `${text + spancol}Base: </span>${feature.properties?.base} ft<br/>`;
  if (feature.properties?.dir !== undefined) {
    if (feature.properties?.spd === 0) text = `${text + spancol}Move: </span>stationary<br/>`;
    else text = `${text + spancol}Move: </span>${feature.properties?.dir}<br/>`;
  }
  if (feature.properties?.spd > 0) text = `${text + spancol}Speed: </span>${feature.properties?.spd} kt<br/>`;
  if (feature.properties?.chng !== undefined) text = `${text + spancol}Change: </span>${feature.properties?.chng}<br/>`;
  if (feature.properties?.geom === "UNK")
    text = `${text + spancol}Region: </span>Undetermined, displaying whole FIR<br/>`;
  text = `${text}<hr style='margin:1px 0 1px 0'/><p>${rawtext}</p></div>`;
  return text;
}

export function getMetarPopupText(feature: mapboxgl.MapboxGeoJSONFeature): string {
  let text = `<div style='font-size:10px; color: black'><div style='background-color: #1150a0; color: white; font-weight: bold'>METAR: ${feature.properties?.id} [${feature.properties?.site}]</div>`;
  const spancol = '<span style="color: #7777CC; font-weight: bold">';
  text = `${text + spancol}METAR: </span>${feature.properties?.rawOb}<br/>`;
  if (feature.properties?.rawTaf) {
    let taf = feature.properties?.rawTaf.replace("FROM", "<br/>&nbsp;&nbsp;FROM");
    taf = taf.replace(/FM0/g, "<br/>&nbsp;&nbsp;FM0");
    taf = taf.replace(/FM1/g, "<br/>&nbsp;&nbsp;FM1");
    taf = taf.replace(/FM2/g, "<br/>&nbsp;&nbsp;FM2");
    taf = taf.replace(/FM3/g, "<br/>&nbsp;&nbsp;FM3");
    taf = taf.replace(/TEMPO/g, "<br/>&nbsp;&nbsp;TEMPO");
    taf = taf.replace(/BECMG/g, "<br/>&nbsp;&nbsp;BECMG");
    taf = taf.replace(/PROB/g, "<br/>&nbsp;&nbsp;PROB");
    taf = taf.replace(/INTER/g, "<br/>&nbsp;&nbsp;INTER");
    text = `${text}<hr style='margin:1px 0 1px 0'/>${spancol}TAF: </span>${taf}<br/>`;
  }
  text = `${text}</div>`;
  return text;
}

export function getTafPopupText(feature: mapboxgl.MapboxGeoJSONFeature): string {
  const spancol = '<span style="color: #7777CC; font-weight: bold">';
  let text = `<div style='font-size:10px; color: black'><div style='background-color: #1150a0; color: white; font-weight: bold'>${feature.properties?.id} [${feature.properties?.site}]</div>`;
  if (feature.properties?.rawOb) {
    text = `${text + spancol}METAR: </span>${feature.properties?.rawOb}<hr style='margin:1px 0 1px 0'/>`;
  }

  let taf = feature.properties?.rawTAF.replace("FROM", "<br/>&nbsp;&nbsp;FROM");
  taf = taf.replace(/FM0/g, "<br/>&nbsp;&nbsp;FM0");
  taf = taf.replace(/FM1/g, "<br/>&nbsp;&nbsp;FM1");
  taf = taf.replace(/FM2/g, "<br/>&nbsp;&nbsp;FM2");
  taf = taf.replace(/FM3/g, "<br/>&nbsp;&nbsp;FM3");
  taf = taf.replace(/TEMPO/g, "<br/>&nbsp;&nbsp;TEMPO");
  taf = taf.replace(/BECMG/g, "<br/>&nbsp;&nbsp;BECMG");
  taf = taf.replace(/PROB/g, "<br/>&nbsp;&nbsp;PROB");
  taf = taf.replace(/INTER/g, "<br/>&nbsp;&nbsp;INTER");
  text = `${text + spancol}TAF: </span>${taf}`;

  return text;
}

export const visibInMetric: string | Expression = [
  "case",
  ["==", ["get", "visib"], null],
  "-",
  ["<=", ["get", "visib"], -990],
  "-",
  ["==", ["get", "visib"], 10.0],
  "16",
  ["==", ["get", "visib"], 6.21],
  "10",
  ["<", ["*", ["get", "visib"], 1.6], 3],
  [
    "number-format",
    ["*", ["get", "visib"], 1.6],
    { "max-fraction-digits": 1 }, //  round toFixed(1)
  ],
  ["number-format", ["*", ["get", "visib"], 1.6], { "min-fraction-digits": 0, "max-fraction-digits": 0 }], //  round toFixed(0)
];

const outVisib = (value: number) => {
  let newValue = value;
  const metric = false;
  if (newValue === undefined || newValue <= -990) return "-";
  if (metric) {
    if (newValue === 10.0) return "16+";
    if (newValue === 6.21) return "10+";
    newValue = 1.6 * newValue;
    if (newValue < 3) return newValue.toFixed(1);
    return newValue.toFixed(0);
  }
  if (newValue === 10.0) return "10+";
  if (newValue === 6.21) return "6+";
  if (newValue < 3) return newValue.toFixed(1);
  return newValue.toFixed(0);
};

const outVisibUnits = () => {
  const metric = false;
  if (metric) return "km";
  return "sm";
};

export const airepIconDirExpression: number | Expression = [
  "case",
  ["==", ["get", "tbInt1"], "EXTM"],
  0,
  ["==", ["get", "tbInt1"], "SEV-EXTM"],
  0,
  ["==", ["get", "tbInt1"], "SEV"],
  0,
  ["==", ["get", "icgInt1"], "SEV"],
  0,
  ["==", ["get", "icgInt1"], "HVY"],
  0,
  ["==", ["get", "tbInt1"], "MOD-SEV"],
  0,
  ["==", ["get", "icgInt1"], "MOD-SEV"],
  0,
  // ["==", ["get", "tbType1"], "MWAVE"],
  //      "MWAV", // text
  // ["==", ["get", "tbType1"], "LLWS"],
  //      "LLWS", // text
  ["==", ["get", "tbInt1"], "MOD"],
  0,
  ["==", ["get", "icgInt1"], "MOD"],
  0,
  ["==", ["get", "tbInt1"], "LGT-MOD"],
  0,
  ["==", ["get", "icgInt1"], "LGT-MOD"],
  0,
  ["==", ["get", "tbInt1"], "LGT"],
  0,
  ["==", ["get", "icgInt1"], "LGT"],
  0,
  ["==", ["get", "tbInt1"], "SMTH-LGT"],
  0,
  ["==", ["get", "icgInt1"], "TRC-LGT"],
  0,
  ["==", ["get", "icgInt1"], "TRC"],
  0,
  ["==", ["get", "tbInt1"], "NEG"],
  0,
  ["==", ["get", "icgInt1"], "NEG"],
  0,
  45,
];

export const airepIconExpression: string | Expression = [
  "case",
  ["==", ["get", "tbInt1"], "EXTM"],
  "airep-wx_TUR-X", //
  ["==", ["get", "tbInt1"], "SEV-EXTM"],
  "airep-wx_TUR-S",
  ["==", ["get", "tbInt1"], "SEV"],
  "airep-wx_TUR-S",
  ["==", ["get", "icgInt1"], "SEV"],
  "airep-wx_ICE-S",
  ["==", ["get", "icgInt1"], "HVY"],
  "airep-wx_ICE-S",
  ["==", ["get", "tbInt1"], "MOD-SEV"],
  "airep-wx_TUR-MS",
  ["==", ["get", "icgInt1"], "MOD-SEV"],
  "airep-wx_ICE-M",
  // ["==", ["get", "tbType1"], "MWAVE"],
  //      "MWAV", // text
  // ["==", ["get", "tbType1"], "LLWS"],
  //      "LLWS", // text
  ["==", ["get", "tbInt1"], "MOD"],
  "airep-wx_TUR-M",
  ["==", ["get", "icgInt1"], "MOD"],
  "airep-wx_ICE-M",

  ["==", ["get", "tbInt1"], "LGT-MOD"],
  "airep-wx_TUR-LM",
  ["==", ["get", "icgInt1"], "LGT-MOD"],
  "airep-wx_ICE-L",

  ["==", ["get", "tbInt1"], "LGT"],
  "airep-wx_TUR-L",
  ["==", ["get", "icgInt1"], "LGT"],
  "airep-wx_ICE-L",

  ["==", ["get", "tbInt1"], "SMTH-LGT"],
  "airep-wx_TUR-TL",
  ["==", ["get", "icgInt1"], "TRC-LGT"],
  "airep-wx_ICE-TL",

  ["==", ["get", "icgInt1"], "TRC"],
  "airep-wx_ICE-T",
  ["==", ["get", "tbInt1"], "NEG"],
  "airep-wx_TUR-N",
  ["==", ["get", "icgInt1"], "NEG"],
  "airep-wx_ICE-N",
  "airep-wx_PLANE",
];

// all others empty
export const wxIconExpression: string | Expression = [
  "case",
  ["==", ["get", "wx"], "FU"],
  "Symbol_code_ww_04", // p3
  ["==", ["get", "wx"], "VA"],
  "Symbol_code_ww_04", // p3
  ["==", ["get", "wx"], "HZ"],
  "Symbol_code_ww_05", // p59
  ["==", ["get", "wx"], "DU"],
  "Symbol_code_ww_06", // p14
  ["==", ["get", "wx"], "SA"],
  "Symbol_code_ww_06", // p14
  ["==", ["get", "wx"], "BLDU"],
  "Symbol_code_ww_07", // p13
  ["==", ["get", "wx"], "BLSA"],
  "Symbol_code_ww_07", // p13
  ["==", ["get", "wx"], "PO"],
  "Symbol_code_ww_08", // p9
  ["==", ["get", "wx"], "VCSS"],
  "Symbol_code_ww_09", // p12

  ["==", ["get", "wx"], "BR"],
  "Symbol_code_ww_10", // p58
  ["==", ["get", "wx"], "MIFG"],
  "Symbol_code_ww_12", // p56
  ["==", ["get", "wx"], "VCTS"],
  "Symbol_code_ww_13", // p50
  ["==", ["get", "wx"], "VIRGA"],
  "Symbol_code_ww_14", // p57
  ["==", ["get", "wx"], "VCSH"],
  "Symbol_code_ww_16", // p51
  ["==", ["get", "wx"], "TS"],
  "Symbol_code_ww_17", // p33
  ["==", ["get", "wx"], "SQ"],
  "Symbol_code_ww_18", // p2
  ["==", ["get", "wx"], "FC"],
  "Symbol_code_ww_19", // p1
  ["==", ["get", "wx"], "+FC"],
  "Symbol_code_ww_19", // p1

  ["==", ["get", "wx"], "SS"],
  "Symbol_code_ww_31", // P11
  ["==", ["get", "wx"], "DS"],
  "Symbol_code_ww_31", // P11
  ["==", ["get", "wx"], "DRSA"],
  "Symbol_code_ww_31", // P11
  ["==", ["get", "wx"], "DRDU"],
  "Symbol_code_ww_31", // P11

  ["==", ["get", "wx"], "+SS"],
  "Symbol_code_ww_34", // p10
  ["==", ["get", "wx"], "+DS"],
  "Symbol_code_ww_34", // p10
  ["==", ["get", "wx"], "BLSN"],
  "Symbol_code_ww_36", // p48
  ["==", ["get", "wx"], "VCBLSN"],
  "Symbol_code_ww_36", // p48
  ["==", ["get", "wx"], "DRSN"],
  "Symbol_code_ww_38", // p47
  //40
  ["==", ["get", "wx"], "VCFG"],
  "Symbol_code_ww_40", // p55
  ["==", ["get", "wx"], "BCFG"],
  "Symbol_code_ww_41", // p54
  ["==", ["get", "wx"], "PRFG"],
  "Symbol_code_ww_44", // p53
  ["==", ["get", "wx"], "FG"],
  "Symbol_code_ww_45", // p52
  ["==", ["get", "wx"], "FZFG"],
  "Symbol_code_ww_49", // p49

  ["==", ["get", "wx"], "-DZ"],
  "Symbol_code_ww_51", // p46
  ["==", ["get", "wx"], "DZ"],
  "Symbol_code_ww_53", // p42
  ["==", ["get", "wx"], "+DZ"],
  "Symbol_code_ww_55", // p39
  ["==", ["get", "wx"], "-FZDZ"],
  "Symbol_code_ww_56", // p22
  ["==", ["get", "wx"], "FZDZ"],
  "Symbol_code_ww_57", // p21
  ["==", ["get", "wx"], "+FZDZ"],
  "Symbol_code_ww_57", // p21

  ["==", ["get", "wx"], "-DZRA"],
  "Symbol_code_ww_58", // p35
  ["==", ["get", "wx"], "DZRA"],
  "Symbol_code_ww_59", // p34

  ["==", ["get", "wx"], "-RA"],
  "Symbol_code_ww_61", // p44
  ["==", ["get", "wx"], "RA"],
  "Symbol_code_ww_63", // p41
  ["==", ["get", "wx"], "+RA"],
  "Symbol_code_ww_65", // p37
  ["==", ["get", "wx"], "-FZRA"],
  "Symbol_code_ww_66", // p24
  ["==", ["get", "wx"], "FZRA"],
  "Symbol_code_ww_67", // p23
  ["==", ["get", "wx"], "+FZRA"],
  "Symbol_code_ww_67", // p23
  ["==", ["get", "wx"], "-RASN"],
  "Symbol_code_ww_68", // p26
  ["==", ["get", "wx"], "RASN"],
  "Symbol_code_ww_69", // p25
  ["==", ["get", "wx"], "+RASN"],
  "Symbol_code_ww_69", // p25

  // 70
  ["==", ["get", "wx"], "-SN"],
  "Symbol_code_ww_71", // p43
  ["==", ["get", "wx"], "SN"],
  "Symbol_code_ww_73", // p40
  ["==", ["get", "wx"], "+SN"],
  "Symbol_code_ww_75", // p36

  ["==", ["get", "wx"], "UP"],
  "Symbol_code_ww_76", // p32
  ["==", ["get", "wx"], "SG"],
  "Symbol_code_ww_77", // p19

  ["==", ["get", "wx"], "IC"],
  "Symbol_code_ww_78", // p31

  ["==", ["get", "wx"], "PE"],
  "Symbol_code_ww_79", // p20

  ["==", ["get", "wx"], "PE PL"],
  "Symbol_code_ww_79", // p20
  ["==", ["get", "wx"], "PL"],
  "Symbol_code_ww_79", // p20

  //80
  ["==", ["get", "wx"], "-SHRA"],
  "Symbol_code_ww_80", // p45
  ["==", ["get", "wx"], "-SH"],
  "Symbol_code_ww_80", // p45
  ["==", ["get", "wx"], "SHRA"],
  "Symbol_code_ww_81", // p38
  ["==", ["get", "wx"], "+SHRA"],
  "Symbol_code_ww_81", // p38
  ["==", ["get", "wx"], "+SH"],
  "Symbol_code_ww_81", // p38
  ["==", ["get", "wx"], "SH"],
  "Symbol_code_ww_81", // p38

  ["==", ["get", "wx"], "-SHRASN"],
  "Symbol_code_ww_83", // p28
  ["==", ["get", "wx"], "SHRASN"],
  "Symbol_code_ww_84", // p27
  ["==", ["get", "wx"], "+SHRASN"],
  "Symbol_code_ww_84", // p27

  ["==", ["get", "wx"], "-SHSN"],
  "Symbol_code_ww_85", // p30
  ["==", ["get", "wx"], "SHSN"],
  "Symbol_code_ww_86", // p29
  ["==", ["get", "wx"], "+SHSN"],
  "Symbol_code_ww_86", // p29

  ["==", ["get", "wx"], "-GS"],
  "Symbol_code_ww_87", // p18
  ["==", ["get", "wx"], "-SHGS"],
  "Symbol_code_ww_87", // p18

  ["==", ["get", "wx"], "GS"],
  "Symbol_code_ww_88", // p16
  ["==", ["get", "wx"], "SHGS"],
  "Symbol_code_ww_88", // p16
  ["==", ["get", "wx"], "+GS"],
  "Symbol_code_ww_88", // p16
  ["==", ["get", "wx"], "+SHGS"],
  "Symbol_code_ww_88", // p16

  ["==", ["get", "wx"], "-GR"],
  "Symbol_code_ww_89", // p17
  ["==", ["get", "wx"], "-SHGR"],
  "Symbol_code_ww_89", // p17

  //90

  ["==", ["get", "wx"], "GR"],
  "Symbol_code_ww_90", // p15
  ["==", ["get", "wx"], "SHGR"],
  "Symbol_code_ww_90", // p15
  ["==", ["get", "wx"], "+GR"],
  "Symbol_code_ww_90", // p15
  ["==", ["get", "wx"], "+SHGR"],
  "Symbol_code_ww_90", // p15

  ["==", ["get", "wx"], "TSRA"],
  "Symbol_code_ww_95", // p8
  ["==", ["get", "wx"], "TSSN"],
  "Symbol_code_ww_95", // p8
  ["==", ["get", "wx"], "TSPL"],
  "Symbol_code_ww_95", // p8

  ["==", ["get", "wx"], "TSGR"],
  "Symbol_code_ww_96", // p7
  ["==", ["get", "wx"], "TSGS"],
  "Symbol_code_ww_96", // p7

  ["==", ["get", "wx"], "+TSRA"],
  "Symbol_code_ww_97", // p6
  ["==", ["get", "wx"], "+TSSN"],
  "Symbol_code_ww_97", // p6
  ["==", ["get", "wx"], "+TSPL"],
  "Symbol_code_ww_97", // p6

  ["==", ["get", "wx"], "TS SA"],
  "Symbol_code_ww_98", // p4 ANY TS and SA
  ["==", ["get", "wx"], "TS DU"],
  "Symbol_code_ww_98", // p4 ANY TS and DU

  ["==", ["get", "wx"], "+TSGS"],
  "Symbol_code_ww_99", // p5
  ["==", ["get", "wx"], "+TSGR"],
  "Symbol_code_ww_99", // p5
  "",
];
